package er.prateek.crossly.di

import er.prateek.crossly.ads.AdFrequencyController
import er.prateek.crossly.ads.AdHostProvider
import er.prateek.crossly.ads.AdsCoordinator
import er.prateek.crossly.ads.entitlement.DefaultEntitlementProvider
import er.prateek.crossly.ads.entitlement.NoPurchaseGateway
import er.prateek.crossly.ads.entitlement.PurchaseGateway
import er.prateek.crossly.config.AppConfig
import er.prateek.crossly.content.ContentRepository
import er.prateek.crossly.content.ContentRepositoryImpl
import er.prateek.crossly.content.RemoteContentSource
import er.prateek.crossly.firebase.AnalyticsLogger
import er.prateek.crossly.firebase.CrashReporter
import er.prateek.crossly.firebase.FirebaseProvider
import er.prateek.crossly.firebase.RemoteConfigClient
import er.prateek.crossly.firebase.RemoteConfigKeys
import er.prateek.crossly.persistence.AdsPrefsStore
import er.prateek.crossly.persistence.CoachPrefsStore
import er.prateek.crossly.persistence.AnalyticsPrefsStore
import er.prateek.crossly.persistence.ContentCacheStore
import er.prateek.crossly.persistence.EntitlementStore
import er.prateek.crossly.persistence.GameplayPrefsStore
import er.prateek.crossly.persistence.ProgressStore
import er.prateek.crossly.persistence.RevealPrefsStore
import er.prateek.crossly.persistence.SettingsAdsPrefsStore
import er.prateek.crossly.persistence.SettingsCoachPrefsStore
import er.prateek.crossly.persistence.SettingsAnalyticsPrefsStore
import er.prateek.crossly.persistence.SettingsContentCacheStore
import er.prateek.crossly.persistence.SettingsEntitlementStore
import er.prateek.crossly.persistence.SettingsGameplayPrefsStore
import er.prateek.crossly.persistence.IncomingChallengeStore
import er.prateek.crossly.persistence.MathSessionStore
import er.prateek.crossly.persistence.NotificationPrefsStore
import er.prateek.crossly.persistence.OutboundShareStore
import er.prateek.crossly.persistence.ProfileStore
import er.prateek.crossly.persistence.SettingsIncomingChallengeStore
import er.prateek.crossly.persistence.SettingsMathSessionStore
import er.prateek.crossly.persistence.SettingsNotificationPrefsStore
import er.prateek.crossly.persistence.SettingsOutboundShareStore
import er.prateek.crossly.persistence.SettingsProfileStore
import er.prateek.crossly.persistence.SettingsProgressStore
import er.prateek.crossly.persistence.SavedSessionStore
import er.prateek.crossly.persistence.SettingsSavedSessionStore
import er.prateek.crossly.persistence.SettingsRevealPrefsStore
import er.prateek.crossly.persistence.SettingsThemeStore
import er.prateek.crossly.persistence.ThemeStore
import er.prateek.crossly.persistence.createSettings
import er.prateek.crossly.game.crossword.RevealLimiter
import er.prateek.crossly.notifications.NotificationCoordinator
import er.prateek.crossly.notifications.NotificationPermissionProvider
import er.prateek.crossly.notifications.NotificationSchedulerProvider
import er.prateek.crossly.data.model.GameMode
import er.prateek.crossly.progression.DailyPuzzleManager
import er.prateek.crossly.progression.ModeProgressionManager
import er.prateek.crossly.progression.ProgressionManager
import er.prateek.crossly.progression.SavedSessionManager
import er.prateek.crossly.share.ShareManager
import er.prateek.crossly.persistence.AchievementStore
import er.prateek.crossly.persistence.SettingsAchievementStore
import er.prateek.crossly.progression.PlayerJourney
import er.prateek.crossly.progression.StreakManager
import er.prateek.crossly.progression.achievements.AchievementManager
import er.prateek.crossly.progression.achievements.PlayerStats
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.launch

object AppContainer {
    private val settings = createSettings()
    val progressStore: ProgressStore = SettingsProgressStore(settings)
    val themeStore: ThemeStore = SettingsThemeStore(settings)
    val contentCacheStore: ContentCacheStore = SettingsContentCacheStore(settings)
    val analyticsPrefsStore: AnalyticsPrefsStore = SettingsAnalyticsPrefsStore(settings)
    val gameplayPrefsStore: GameplayPrefsStore = SettingsGameplayPrefsStore(settings)
    val coachPrefsStore: CoachPrefsStore = SettingsCoachPrefsStore(settings)
    val adsPrefsStore: AdsPrefsStore = SettingsAdsPrefsStore(settings)
    val revealPrefsStore: RevealPrefsStore = SettingsRevealPrefsStore(settings)
    val entitlementStore: EntitlementStore = SettingsEntitlementStore(settings)
    val profileStore: ProfileStore = SettingsProfileStore(settings)
    val outboundShareStore: OutboundShareStore = SettingsOutboundShareStore(settings)
    val incomingChallengeStore: IncomingChallengeStore = SettingsIncomingChallengeStore(settings)
    val savedSessionStore: SavedSessionStore = SettingsSavedSessionStore(settings)
    val mathSessionStore: MathSessionStore = SettingsMathSessionStore(settings)
    val notificationPrefsStore: NotificationPrefsStore = SettingsNotificationPrefsStore(settings)
    val achievementStore: AchievementStore = SettingsAchievementStore(settings)

    /** For fire-and-forget work that must outlive the screen that triggered it (reminder reschedules). */
    private val appScope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    val analytics: AnalyticsLogger = FirebaseProvider.analytics
    val crashReporter: CrashReporter = FirebaseProvider.crashReporter
    val remoteConfig: RemoteConfigClient = FirebaseProvider.remoteConfig

    /**
     * No paid tier is on sale yet, so nothing can grant an entitlement and every user is on
     * the ad-supported path. This is the single line to change when one ships: bind a Play
     * Billing / StoreKit gateway here and the "Remove ads" UI, the entitlement cache and
     * every ad gate in the app start working against it -- no other file is involved.
     */
    private val purchaseGateway: PurchaseGateway = NoPurchaseGateway

    val entitlementProvider = DefaultEntitlementProvider(
        store = entitlementStore,
        gateway = purchaseGateway,
    )

    val adsCoordinator = AdsCoordinator(
        remoteConfig = remoteConfig,
        entitlementProvider = entitlementProvider,
        frequency = AdFrequencyController(adsPrefsStore),
        host = AdHostProvider.adHost,
        analytics = analytics,
        personalizationAllowed = { analyticsPrefsStore.isEnabled() },
    )

    val revealLimiter = RevealLimiter(revealPrefsStore)

    // No production bucket has been provisioned yet -- Phase 5 only builds the
    // client-side plumbing (RemoteContentSource, merge-by-version, checksum,
    // cache/fallback), verified against a mocked HTTP engine in tests. Once a
    // real manifest.json/pack bucket exists, point this at its base URL.
    private val remoteContentSource: RemoteContentSource? = null

    val contentRepository: ContentRepository = ContentRepositoryImpl(
        cache = contentCacheStore,
        remote = remoteContentSource,
    )

    val shareManager: ShareManager = ShareManager(
        profile = profileStore,
        outbound = outboundShareStore,
        incoming = incomingChallengeStore,
        content = contentRepository,
        analytics = analytics,
        riddlesEnabled = { appConfig().modes.riddles.enabled },
    )

    val progressionManager: ProgressionManager = ProgressionManager(progressStore, remoteConfig)

    /** The live, parsed `app_config` -- read fresh so a mid-session Remote Config change is seen. */
    fun appConfig(): AppConfig = AppConfig.parse(remoteConfig.getString(RemoteConfigKeys.APP_CONFIG))

    /** Per-mode progression for the leveled math modes. Levels-per-tier is read from live config. */
    val mathProgressionManager: ModeProgressionManager = ModeProgressionManager(
        mode = GameMode.MATH_CROSSWORD,
        store = progressStore,
        levelsPerTier = { appConfig().modes.mathCrossword.levelsPerTier },
        remoteConfig = remoteConfig,
    )
    val riddleProgressionManager: ModeProgressionManager = ModeProgressionManager(
        mode = GameMode.RIDDLES,
        store = progressStore,
        levelsPerTier = { appConfig().modes.riddles.levelsPerTier },
        remoteConfig = remoteConfig,
    )

    val savedSessionManager: SavedSessionManager = SavedSessionManager(savedSessionStore)
    val dailyPuzzleManager: DailyPuzzleManager = DailyPuzzleManager(
        store = progressStore,
        contentRepository = contentRepository,
        contentCache = contentCacheStore,
        remote = remoteContentSource,
    )

    val streakManager: StreakManager = StreakManager(
        store = progressStore,
        config = { appConfig().streak },
    )

    val achievementManager: AchievementManager = AchievementManager(achievementStore, analytics)

    /** Every finished game, in every mode, reports here: streak, badges, reminder refresh. */
    val playerJourney: PlayerJourney = PlayerJourney(
        streak = streakManager,
        achievements = achievementManager,
        stats = {
            PlayerStats(
                wordSolved = progressionManager.completedPuzzleCount(),
                mathSolved = mathProgressionManager.completedCount(),
                riddleSolved = riddleProgressionManager.completedCount(),
                dailySolved = dailyPuzzleManager.currentStreak().lastCompletedDate != null,
                perfectSolves = progressionManager.perfectPuzzleCount() +
                    mathProgressionManager.perfectCount() + riddleProgressionManager.perfectCount(),
                longestStreak = streakManager.streak.longest,
                brainstormRounds = achievementManager.brainstormRounds(),
                streakRestores = streakManager.streak.restoresUsed,
            )
        },
        analytics = analytics,
        onProgressChanged = { appScope.launch { notificationCoordinator.onProgressChanged() } },
    )

    val notificationCoordinator = NotificationCoordinator(
        prefsStore = notificationPrefsStore,
        dailyPuzzleManager = dailyPuzzleManager,
        streakManager = streakManager,
        remoteConfig = remoteConfig,
        permission = NotificationPermissionProvider.controller,
        scheduler = NotificationSchedulerProvider.scheduler,
        analytics = analytics,
        sessionCount = { profileStore.sessionCount() },
        puzzlesCompleted = { progressionManager.completedPuzzleCount() },
    )

    /**
     * Settings -> Reset progress: every mode's stars and unlocks, both streaks, and all badges.
     * They go together because badges are derived from progress -- clearing badges but keeping,
     * say, math solves would just re-award those badges on the next check.
     */
    fun resetAllProgress() {
        progressionManager.resetProgress()
        mathProgressionManager.resetProgress()
        riddleProgressionManager.resetProgress()
        dailyPuzzleManager.resetStreak()
        streakManager.reset()
        achievementManager.reset()
        savedSessionManager.clear()
        mathSessionStore.save(null)
        appScope.launch { notificationCoordinator.onProgressChanged() }
    }
}
